/* eslint-disable max-len */
import * as d3 from 'd3';
import * as d3_save_svg from 'd3-save-svg';
import { UseMutationResult } from 'react-query';
import { LocalFilterOptions } from 'src/components/organisms';
import { CreateAuditEntryMutation, Exact } from 'src/graphql';
import { AuditActions, State } from 'src/types';
import { trackChartDownloaded } from 'src/utils/analytics';
import * as XLSX from 'xlsx';
import { sanitizeCSVData } from '../csvsecurity';
import { DateFormats, formatDate } from '../time';
import { createDataWorksheet, createFiltersWorksheet } from './createWorksheet';
import { DOWNLOAD_TYPE } from './insightsTypes';
import { processFiltersForAuditLog } from './processData';
import { CombinedProps } from './saveData.types';

type MutationFunction = UseMutationResult<
  CreateAuditEntryMutation,
  unknown,
  Exact<{
    auditAction: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    auditData: any;
    completed: boolean;
  }>,
  unknown
>;

export const saveSvg = (props: Pick<CombinedProps, 'svgId'>) => {
  d3_save_svg.save(d3.select(`#${props.svgId}`).node(), {
    filename: `${props.svgId} ${formatDate(
      new Date(),
      DateFormats.DD_MM_YYYY_TIME,
    )}`,
  });
};

const saveXlsx = (
  props: Pick<CombinedProps, 'data' | 'title' | 'filters' | 'dataType'>,
  selectionFilters: LocalFilterOptions | undefined,
) => {
  const { data, title } = props;
  const workbook = XLSX.utils.book_new();

  if (data?.length) {
    createDataWorksheet(data, props, workbook);
  }

  if (selectionFilters) {
    createFiltersWorksheet(selectionFilters, workbook);
  }

  XLSX.writeFile(
    workbook,
    `${title}_${formatDate(new Date(), DateFormats.DD_MMM_YYYY_DASH)}.xlsx`,
  );
};

const saveCsv = (
  props: Pick<CombinedProps, 'data' | 'title' | 'filters' | 'dataType'>,
  selectionFilters: LocalFilterOptions | undefined,
) => {
  const { data, title } = props;
  const dataWorkbook = XLSX.utils.book_new();
  const filtersWorkbook = XLSX.utils.book_new();

  if (data?.length) {
    // Sanitize data for CSV security
    const { sanitizedData } = sanitizeCSVData(data);

    // Use sanitized data for worksheet creation
    // const sanitizedProps = { ...props, data: sanitizedData };
    createDataWorksheet(sanitizedData, props, dataWorkbook);

    XLSX.writeFile(
      dataWorkbook,
      `${title}_${formatDate(
        new Date(),
        DateFormats.DD_MMM_YYYY_DASH,
      )}-data.csv`,
      {
        bookType: 'csv',
      },
    );
  }

  if (selectionFilters) {
    createFiltersWorksheet(selectionFilters, filtersWorkbook);

    XLSX.writeFile(
      filtersWorkbook,
      `${title}_${formatDate(
        new Date(),
        DateFormats.DD_MMM_YYYY_DASH,
      )}-filters.csv`,
      {
        bookType: 'csv',
      },
    );
  }
};

export const saveData = (
  downloadType: DOWNLOAD_TYPE,
  organisation: State['organisation']['organisation'],
  segmentEnabled: boolean,
  selectionFilters: LocalFilterOptions | undefined,
  props: Pick<
    CombinedProps,
    'data' | 'title' | 'filters' | 'dataType' | 'svgId' | 'dashboard'
  >,
  createAuditEntry: MutationFunction,
  printChart?: () => void,
) => {
  switch (downloadType) {
    case DOWNLOAD_TYPE.PDF:
      if (printChart) {
        printChart();
      } else {
        saveSvg(props);
      }
      break;
    case DOWNLOAD_TYPE.XLSX:
      saveXlsx(props, selectionFilters);
      break;
    case DOWNLOAD_TYPE.XLS:
      saveXlsx(props, selectionFilters);
      break;
    case DOWNLOAD_TYPE.CSV:
      saveCsv(props, selectionFilters);
      break;
  }

  const filtersApplied = processFiltersForAuditLog(props.filters);
  createAuditEntry.mutate({
    auditAction: AuditActions.DOWNLOAD_INSIGHTS_CHART,
    auditData: {
      graph: props.svgId,
      filtersApplied,
      downloadType,
    },
    completed: true,
  });

  if (organisation?.id && segmentEnabled) {
    trackChartDownloaded(
      organisation.id,
      props.dashboard,
      props.svgId,
      downloadType,
    );
  }
};
